import {FocusMonitor} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {Component, DoCheck, ElementRef, Injector, Input, OnDestroy, OnInit, Optional, Self, ViewChild} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormGroup, NgControl, Validators} from '@angular/forms';
import {MatFormFieldControl} from '@angular/material/form-field';
import {Subject} from 'rxjs';
import {debounce} from "utils-decorators";
import {ITimeCounter} from "../../interfaces";

export class TimeCounter {
    constructor(public hours: number, public minutes: number, public seconds: number, public unix: number) {
    }
}

@Component({
    selector: 'app-input-time',
    templateUrl: 'input-time.component.html',
    styleUrls: ['input-time.component.scss'],
    providers: [{provide: MatFormFieldControl, useExisting: InputTimeComponent}],
    host: {
        '[class.example-floating]': 'shouldLabelFloat',
        '[id]': 'id',
        '[attr.aria-describedby]': 'describedBy',
    }
})
export class InputTimeComponent implements ControlValueAccessor, OnInit, DoCheck, MatFormFieldControl<TimeCounter>, OnDestroy {

    static nextId = 0;
    static ngAcceptInputType_disabled: boolean | string | null | undefined;
    static ngAcceptInputType_required: boolean | string | null | undefined;

    @ViewChild('inputHours', {static: true}) inputHours: ElementRef;
    @ViewChild('inputMinutes', {static: true}) inputMinutes: ElementRef;
    @ViewChild('inputSeconds', {static: true}) inputSeconds: ElementRef;


    MAX_HOUR = 24;
    private savedDate;
    private _placeholder: string;
    private _disabled = false;
    private _required = false;
    parts: FormGroup;
    stateChanges = new Subject<void>();
    focused = false;
    errorState = false;
    controlType = 'example-tel-input';
    id = `example-tel-input-${InputTimeComponent.nextId++}`;
    describedBy = '';
    m_minTime = 0;
    onChange = (_: any) => {
    };
    onTouched = () => {
    };

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }

    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }

    @Input()
    set maxValue(i_value: any) {
        this.MAX_HOUR = i_value;
    }

    get maxValue(): any {
        return this.MAX_HOUR;
    }

    @Input()
    get required(): boolean {
        return this._required;
    }

    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.parts.disable() : this.parts.enable();
        this.stateChanges.next();
    }

    @Input()
    get value(): TimeCounter | null {
        if (this.parts.valid) {
            const {value: {hours, minutes, seconds, unix}} = this.parts;
            // const dateObject = new Date(Number(unix));
            return new TimeCounter(hours, minutes, seconds, unix);
        }
        return null;
    }

    set value(i_time: TimeCounter | ITimeCounter | null | string) {
        if (String(i_time).match(/^([^S]){1,2}:([^S]){1,2}:([^S]){1,2}/)) {
            const h = (i_time as any).split(':')[0];
            const m = (i_time as any).split(':')[1];
            const s = (i_time as any).split(':')[2];
            i_time = new TimeCounter(h, m, s, -1);
        }
        if (!(i_time instanceof TimeCounter)) return;
        i_time = new TimeCounter(i_time.hours, i_time.minutes, i_time.seconds, i_time.unix);
        this.parts.setValue({hours: i_time.hours, minutes: i_time.minutes, seconds: i_time.seconds, unix: i_time.unix});
        this.fireChanges();
    }

    constructor(private _focusMonitor: FocusMonitor,
                formBuilder: FormBuilder,
                private _elementRef: ElementRef<HTMLElement>,
                public elRef: ElementRef, public injector: Injector, @Optional() @Self()
                public ngControl: NgControl) {

        this.parts = formBuilder.group({
            hours: [null, [Validators.required, Validators.minLength(1), Validators.maxLength(2)]],
            minutes: [null, [Validators.required, Validators.minLength(1), Validators.maxLength(2)]],
            seconds: [null, [Validators.required, Validators.minLength(1), Validators.maxLength(2)]],
            unix: [],
        });

        _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
            if (this.focused && !origin) {
                this.onTouched();
            }
            this.focused = !!origin;
            this.stateChanges.next();
        });
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit() {
        // this.ngControl = this.injector.get(NgControl);
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    ngDoCheck(): void {
        if (this.ngControl) {
            this.errorState = this.ngControl.invalid && this.ngControl.touched;
            this.stateChanges.next();
        }
    }

    @debounce(50)
    fireChanges(): void {
        this.stateChanges.next();
    }

    get empty() {
        const {value: {hours, minutes, seconds}} = this.parts;
        return !hours && !minutes && !seconds;
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    _handleInput(event, from): void {
        this.onChange(this.value);
    }

    setDescribedByIds(ids: string[]) {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(event: MouseEvent) {
        if ((event.target as Element).tagName.toLowerCase() != 'input') {
            // tslint:disable-next-line
            this._elementRef.nativeElement.querySelector('input')!.focus();
        }
    }

    writeValue(tel: TimeCounter | null): void {
        this.value = tel;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onBlur() {
        this.updateValues();
    }

    onNumericStepper() {
        this.updateValues();
    }

    updateValues() {
        let h = this.inputHours.nativeElement.value;
        let m = this.inputMinutes.nativeElement.value;
        let s = this.inputSeconds.nativeElement.value;
        if (h >= this.MAX_HOUR) h = this.MAX_HOUR;
        h = String(h);
        if (h.length == 1) h = `0${h}`;

        if (m > 59) m = 59;
        if (m.length == 1) m = `0${m}`;

        if (s > 59) s = 59;
        if (s.length == 1) s = `0${s}`;

        if (h == this.MAX_HOUR) {
            h = '00';
            m = '00';
            s = '00';
        }
        this.value = new TimeCounter(h, m, s, -1);
        // this.saveValueSeconds(Number(h) * 3600 + Number(m) * 60 + Number(s));
    }
}
